import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle, ArrowRight, AlertCircle, Target, CheckCircle2, Rocket, RefreshCw, Brain, Crosshair, Scale, Swords, Zap, MessageSquare, FileText, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useEdgeFunctionRetry } from "@/hooks/useEdgeFunctionRetry";
import { ErrorRecovery } from "@/components/ui/error-recovery";
import { logStartFlowEvent } from "@/lib/telemetry/startFlow";
import { useOnboardingStore } from "@/stores/onboardingStore";
import quentariLogo from "@/assets/quentari-logo.svg";

interface StepAnalysisPreviewProps {
  workspaceId: string;
  role: string;
  title: string | null;
  decisionType: string;
  decisionSubtype: string;
  trigger: string;
  stakes: string;
  previousAttempts: string;
  impactScore: number;
  urgencyScore: number;
  impactMetric?: string;
  urgencyReason?: string;
  systemClassifiedType?: string | null;
  systemClassifiedSubtype?: string | null;
  classificationConfidence?: number | null;
  classificationOverridden?: boolean;
  classificationInputHash?: string | null;
  classificationModel?: string | null;
  startFlowId: string;
  onEditStep?: (stepIndex: number) => void;
}

interface AnalysisResult {
  sessionId: string;
  portalSummary: string;
  initialTension: string;
  firstQuestion: string;
  paraphrase: string;
  confidenceLevel: number;
  llmRetryCounts?: {
    initial: number;
    specificity: number;
    length: number;
  };
  // Masterclass fields
  dominantDimension?: string;
  whyThisQuestion?: string;
  frameTitle?: string;
  tensionPairLeft?: string;
  tensionPairRight?: string;
}

type AnalysisPhase = "context" | "analyzing" | "question" | "complete";

// Loading timeline steps
const LOADING_STEPS = [
  {
    id: "extract",
    label: "Optionen validieren",
    description: "Eingaben werden strukturiert und verdichtet",
    icon: Brain,
    delayMs: 0,
  },
  {
    id: "risk",
    label: "Risiko- & Reversibilitätsprofil verdichten",
    description: "Konsequenzen und Umkehrbarkeit werden bewertet",
    icon: Scale,
    delayMs: 800,
  },
  {
    id: "tension",
    label: "Kernspannung extrahieren",
    description: "Der strategische Konflikt wird identifiziert",
    icon: Swords,
    delayMs: -1, // triggered by LLM call start
  },
  {
    id: "question",
    label: "Kernfrage generieren",
    description: "Die strategische Kernfrage wird formuliert",
    icon: MessageSquare,
    delayMs: -1, // triggered by LLM response
  },
  {
    id: "frame",
    label: "Entscheidungsrahmen formulieren",
    description: "Alles wird zu einem klaren Rahmen verdichtet",
    icon: FileText,
    delayMs: -1, // triggered after question step
  },
];

export const StepAnalysisPreview = ({
  workspaceId,
  role,
  title,
  decisionType,
  decisionSubtype,
  trigger,
  stakes,
  previousAttempts,
  impactScore,
  urgencyScore,
  impactMetric,
  urgencyReason,
  systemClassifiedType,
  systemClassifiedSubtype,
  classificationConfidence,
  classificationOverridden,
  classificationInputHash,
  classificationModel,
  startFlowId,
  onEditStep,
}: StepAnalysisPreviewProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [displayedSummary, setDisplayedSummary] = useState("");
  const [displayedTension, setDisplayedTension] = useState("");
  const [displayedQuestion, setDisplayedQuestion] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>("context");
  const [progress, setProgress] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [activeLoadingStep, setActiveLoadingStep] = useState(0);
  const [longWait, setLongWait] = useState(false);

  // Show "noch ein Moment" after 8s of loading
  useEffect(() => {
    if (!isAnalyzing) { setLongWait(false); return; }
    const timer = setTimeout(() => setLongWait(true), 8000);
    return () => clearTimeout(timer);
  }, [isAnalyzing]);

  // Get store data for option display and input counting
  const { optionA, optionB, decisionScope, stakesWrongChoiceA, stakesWrongChoiceB, stakesInaction, stakesWorstCase, reversibilityA, reversibilityB, stakesTimeframeA, stakesTimeframeB, stakesInactionTimeframe, stakesWorstCaseProbability, stakesWorstCaseTrigger, classificationSignals, affectedRoles, initialPreference, timeHorizonDays, riskToleranceLevel, successDefinition, primaryMetric } = useOnboardingStore();

  // Count actual non-empty inputs
  const inputCount = [optionA, optionB, decisionScope, stakesWrongChoiceA, stakesWrongChoiceB, stakesInaction, stakesWorstCase, previousAttempts].filter(Boolean).length;

  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { execute, retry, state: retryState, canRetry } = useEdgeFunctionRetry<AnalysisResult>();

  // Lock body scroll when transition is active
  useEffect(() => {
    if (showTransition) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTransition]);

  // Typing effect hook
  const useTypingEffect = (text: string, speed: number = 30) => {
    const [displayed, setDisplayed] = useState("");
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
      if (!text) return;
      
      let index = 0;
      setDisplayed("");
      setIsComplete(false);

      const timer = setInterval(() => {
        if (index < text.length) {
          setDisplayed(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);

      return () => clearInterval(timer);
    }, [text, speed]);

    return { displayed, isComplete };
  };

  const summaryTyping = useTypingEffect(displayedSummary, 20);
  const tensionTyping = useTypingEffect(displayedTension, 25);
  const questionTyping = useTypingEffect(displayedQuestion, 30);

  const callPhase1Start = useCallback(async (): Promise<AnalysisResult> => {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error("Auth error:", authError);
      throw new Error("AUTH_ERROR");
    }

    // Advance loading step to "tension" when LLM call starts
    setActiveLoadingStep(2);

    const response = await supabase.functions.invoke("phase1-start", {
      body: {
        workspace_id: workspaceId,
        user_id: user.id,
        role: role,
        title: title || null,
        decision_type: decisionType,
        decision_subtype: decisionSubtype,
        trigger: trigger,
        stakes: stakes,
        structured_stakes: {
          optionA: {
            wrong_choice_impact: stakesWrongChoiceA || '',
            reversibility: reversibilityA || '',
            timeframe_months: stakesTimeframeA || '6',
          },
          optionB: {
            wrong_choice_impact: stakesWrongChoiceB || '',
            reversibility: reversibilityB || '',
            timeframe_months: stakesTimeframeB || '6',
          },
          inaction: {
            cost: stakesInaction || '',
            timeframe_months: stakesInactionTimeframe || '6',
          },
          worst_case: {
            scenario: stakesWorstCase || '',
            probability: stakesWorstCaseProbability || 'mittel',
            trigger: stakesWorstCaseTrigger || '',
          },
        },
        previous_attempts: previousAttempts,
        impact_score: impactScore,
        urgency_score: urgencyScore,
        impact_metric: impactMetric || null,
        urgency_reason: urgencyReason || null,
        start_request_id: startFlowId,
        classification_signals: classificationSignals || [],
        affected_roles: affectedRoles || [],
        initial_preference: initialPreference || 'undecided',
        // Step 2.3: Structured onboarding fields
        time_horizon_days: timeHorizonDays || null,
        risk_tolerance_level: riskToleranceLevel || null,
        success_definition: successDefinition || null,
        primary_metric: primaryMetric || null,
        classification_audit: {
          system_classified_type: systemClassifiedType || null,
          system_classified_subtype: systemClassifiedSubtype || null,
          confidence: classificationConfidence ?? null,
          was_override: classificationOverridden ?? false,
          final_type: decisionType,
          final_subtype: decisionSubtype,
          input_hash: classificationInputHash || null,
          model: classificationModel || null,
        },
      },
    });

    // Advance to "question" then "frame" when response arrives
    setActiveLoadingStep(3);
    await new Promise(resolve => setTimeout(resolve, 400));
    setActiveLoadingStep(4);

    if (response.error) {
      console.error("Error starting session:", response.error);
      throw new Error(response.error.message || "Edge Function Fehler");
    }

    const data = response.data;

    if (!data.session_id) {
      throw new Error("Keine Session-ID erhalten");
    }

    const analysisData = data.analysis || {};
    return {
      sessionId: data.session_id,
      portalSummary: analysisData.portal_summary || `Du bist ${role} und stehst vor einer Entscheidung im Bereich ${decisionType}.`,
      initialTension: analysisData.initial_tension || "Diese Entscheidung erfordert sorgfältige Abwägung verschiedener Faktoren.",
      firstQuestion: data.first_question_text || "Welche unbelegte Annahme hinter deinen Optionen würde ein konkreter Test in 14 Tagen widerlegen?",
      paraphrase: analysisData.paraphrase || '',
      confidenceLevel: analysisData.confidence_level || 70,
      llmRetryCounts: data.llm_retry_counts || undefined,
      dominantDimension: analysisData.dominant_dimension || 'signal',
      whyThisQuestion: analysisData.why_this_question || '',
      frameTitle: analysisData.frame_title || '',
      tensionPairLeft: analysisData.tension_pair_left || '',
      tensionPairRight: analysisData.tension_pair_right || '',
    };
  }, [workspaceId, role, title, decisionType, decisionSubtype, trigger, stakes, previousAttempts, impactScore, urgencyScore]);

  const performAnalysis = useCallback(async () => {
    setIsAnalyzing(true);
    setHasFailed(false);
    setAnalysisPhase("context");
    setProgress(0);
    setActiveLoadingStep(0);
    
    try {
      // Step 0: "Kontext extrahieren" - immediate
      await new Promise(resolve => setTimeout(resolve, 400));
      setProgress(25);

      // Step 1: "Risikoprofil gewichten" - after 800ms
      setActiveLoadingStep(1);
      setAnalysisPhase("analyzing");
      await new Promise(resolve => setTimeout(resolve, 400));
      setProgress(50);

      // Steps 2 & 3 are triggered inside callPhase1Start
      const analysisResult = await execute(callPhase1Start);

      setProgress(75);
      setAnalysisPhase("question");
      await new Promise(resolve => setTimeout(resolve, 400));
      setProgress(100);

      await new Promise(resolve => setTimeout(resolve, 500));
      setAnalysisPhase("complete");

      setAnalysis(analysisResult);
      setIsAnalyzing(false);

      logStartFlowEvent({
        event: 'phase1_start_success',
        workspaceId,
        metadata: {
          session_id: analysisResult.sessionId,
          confidence: analysisResult.confidenceLevel,
          llm_retries: analysisResult.llmRetryCounts,
        },
      });
      
      setTimeout(() => setShowContent(true), 300);
      setTimeout(() => setDisplayedSummary(analysisResult.portalSummary), 500);
      setTimeout(() => setDisplayedTension(analysisResult.initialTension), 
        500 + analysisResult.portalSummary.length * 20 + 800);
      setTimeout(() => setDisplayedQuestion(analysisResult.firstQuestion), 
        500 + analysisResult.portalSummary.length * 20 + 800 + analysisResult.initialTension.length * 25 + 800);

    } catch (error: any) {
      console.error("Error in performAnalysis:", error);
      setIsAnalyzing(false);
      setHasFailed(true);

      logStartFlowEvent({
        event: 'phase1_start_fail',
        workspaceId,
        metadata: { error: error?.message, retry_count: retryState.retryCount },
      });
      
      if (error.message === "AUTH_ERROR") {
        toast({
          title: "Fehler",
          description: "Du musst angemeldet sein.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      if (!retryState.isRetrying) {
        toast({
          title: "Verbindungsfehler",
          description: "Die Analyse konnte nicht gestartet werden. Du kannst es erneut versuchen.",
          variant: "destructive",
        });
      }
    }
  }, [execute, callPhase1Start, toast, navigate, retryState.isRetrying, workspaceId]);

  const handleRetry = useCallback(async () => {
    setHasFailed(false);
    await performAnalysis();
  }, [performAnalysis]);

  useEffect(() => {
    performAnalysis();
  }, []);

  const handleConfirm = () => {
    if (analysis?.sessionId && !isConfirming) {
      setIsConfirming(true);
      
      setTimeout(() => {
        setShowTransition(true);
      }, 400);
      
      setTimeout(() => {
        // Release the start-flow tab lock before navigating
        try { localStorage.removeItem('quentari_start_flow_lock'); } catch {}
        
        toast({
          title: "Session gestartet",
          description: "Deine Entscheidungs-Session wurde erfolgreich erstellt.",
        });
        navigate(`/app/session/${analysis.sessionId}`, {
          state: {
            firstQuestion: analysis.firstQuestion,
            paraphrase: analysis.paraphrase,
            questionId: 'q_problem_initial'
          }
        });
      }, 1200);
    }
  };

  const handleEditTrigger = () => {
    toast({
      title: "Optionen korrigieren",
      description: "Du kannst die Entscheidung anpassen.",
    });
    onEditStep?.(0);
  };

  const handleEditStakes = () => {
    toast({
      title: "Stakes anpassen",
      description: "Du kannst die Konsequenzen anpassen.",
    });
    onEditStep?.(1);
  };

  // Error State with Retry
  if (hasFailed && !isAnalyzing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-8 animate-fade-in">
        <div className="w-full max-w-md space-y-6">
          <ErrorRecovery
            error={retryState.lastError}
            isRetrying={retryState.isRetrying}
            retryCount={retryState.retryCount}
            isOffline={retryState.isOffline}
            onRetry={handleRetry}
          />
          
          {!retryState.isRetrying && !retryState.isOffline && (
            <div className="text-center space-y-4">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-foreground">
                    Verbindung fehlgeschlagen
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Die Analyse konnte nicht gestartet werden. Das kann an einer instabilen Verbindung oder einem Server-Timeout liegen.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={handleRetry} 
                  className="gap-2"
                  size="lg"
                >
                  <RefreshCw className="h-4 w-4" />
                  Erneut versuchen
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => onEditStep?.(0)}
                  className="text-muted-foreground"
                >
                  Zurück zur Bearbeitung
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // LOADING STATE: Transparent Intelligence Timeline
  // ═══════════════════════════════════════════
  if (isAnalyzing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-8 animate-fade-in relative overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
          <div 
            className="absolute top-0 left-0 w-full h-full opacity-[0.05]"
            style={{
              backgroundImage: `radial-gradient(ellipse 60% 40% at 20% 30%, hsl(var(--primary)) 0%, transparent 50%)`,
            }}
          />
        </div>

        {/* Retry / Offline banners */}
        {retryState.isRetrying && (
          <div className="w-full max-w-md mb-4 relative z-10">
            <ErrorRecovery
              error={null}
              isRetrying={retryState.isRetrying}
              retryCount={retryState.retryCount}
              isOffline={retryState.isOffline}
              onRetry={handleRetry}
            />
          </div>
        )}
        {retryState.isOffline && (
          <div className="w-full max-w-md mb-4 relative z-10">
            <ErrorRecovery
              error={null}
              isRetrying={false}
              retryCount={0}
              isOffline={true}
              onRetry={handleRetry}
            />
          </div>
        )}

        {/* Logo & Tagline */}
        <div className="relative z-10 space-y-3 text-center">
          <img 
            src={quentariLogo} 
            alt="Quentari" 
            className="h-10 w-auto mx-auto"
          />
          <p className="text-sm text-muted-foreground font-medium">
            {retryState.isRetrying 
              ? `Automatischer Retry ${retryState.retryCount}/3...`
              : longWait
                ? "Noch einen Moment – dein Entscheidungsrahmen wird gerade finalisiert"
                : `Wir verdichten ${inputCount} Eingaben zu einem klaren Entscheidungsrahmen`
            }
          </p>
        </div>

        {/* Vertical Timeline Steps */}
        <div className="relative z-10 w-full max-w-md space-y-3">
          {LOADING_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === activeLoadingStep;
            const isCompleted = index < activeLoadingStep;
            
            return (
              <div 
                key={step.id}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-xl border transition-all duration-500",
                  isActive 
                    ? "bg-primary/5 border-primary/30 shadow-lg shadow-primary/5" 
                    : isCompleted 
                      ? "bg-accent/50 border-accent" 
                      : "bg-muted/30 border-border/50 opacity-50"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300",
                  isActive 
                    ? "bg-primary/15 text-primary" 
                    : isCompleted 
                      ? "bg-accent text-accent-foreground" 
                      : "bg-muted text-muted-foreground"
                )}>
                  {isCompleted ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <Icon className={cn("h-5 w-5", isActive && "animate-pulse")} />
                  )}
                </div>

                <div className="flex-1 text-left min-w-0">
                  <p className={cn(
                    "text-sm font-semibold transition-colors",
                    isActive 
                      ? "text-foreground" 
                      : isCompleted 
                        ? "text-accent-foreground" 
                        : "text-muted-foreground"
                  )}>
                    {step.label}
                  </p>
                  {isActive && (
                    <p className="text-xs text-muted-foreground mt-0.5 animate-fade-in">
                      {longWait ? "Noch einen Moment…" : step.description}
                    </p>
                  )}
                </div>

                <div className={cn(
                  "text-xs font-bold shrink-0 transition-colors",
                  isActive 
                    ? "text-primary" 
                    : isCompleted 
                      ? "text-accent-foreground" 
                      : "text-muted-foreground/50"
                )}>
                  {index + 1}/{LOADING_STEPS.length}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!analysis || !showContent) {
    return null;
  }

  const getConfidenceLabel = (level: number) => {
    if (level < 40) return { label: "Niedrig", color: "text-amber-500", bg: "bg-amber-500/10" };
    if (level < 60) return { label: "Mittel", color: "text-blue-500", bg: "bg-blue-500/10" };
    if (level < 80) return { label: "Hoch", color: "text-green-500", bg: "bg-green-500/10" };
    return { label: "Sehr hoch", color: "text-emerald-600", bg: "bg-emerald-500/10" };
  };

  const confidenceLabel = analysis ? getConfidenceLabel(analysis.confidenceLevel) : null;

  // Extract tension as a pairing: try to find "vs" / "oder" pattern, else split at midpoint
  const getTensionPair = (tension: string): { left: string; right: string } | null => {
    if (!tension) return null;
    // Try splitting on common separators
    const separators = [' vs. ', ' vs ', ' oder ', ' gegen '];
    for (const sep of separators) {
      const idx = tension.toLowerCase().indexOf(sep);
      if (idx > 0) {
        return {
          left: tension.slice(0, idx).trim().replace(/^["„]|[""]$/g, ''),
          right: tension.slice(idx + sep.length).trim().replace(/^["„]|[""]$/g, ''),
        };
      }
    }
    // Fallback: split sentences
    const sentences = tension.split(/[.!?]\s+/).map(s => s.trim()).filter(Boolean);
    if (sentences.length >= 2) {
      return { left: sentences[0], right: sentences[1] };
    }
    return null;
  };

  // Use LLM-generated tension pair if available, else parse from text
  const tensionPair = analysis?.tensionPairLeft && analysis?.tensionPairRight
    ? { left: analysis.tensionPairLeft, right: analysis.tensionPairRight }
    : displayedTension ? getTensionPair(displayedTension) : null;

  // ═══════════════════════════════════════════
  // RESULT STATE: Strategic Gravity
  // ═══════════════════════════════════════════
  return (
    <>
      {/* Enterprise-Level Fullscreen Transition Overlay */}
      {showTransition && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[100] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
          <div className="absolute inset-0 backdrop-blur-xl bg-background/95" />
          
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-2 h-2 rounded-full bg-primary/30 animate-glow-pulse"
                style={{ 
                  left: `${10 + i * 12}%`, 
                  top: `${15 + (i % 3) * 25}%`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
          </div>
          
          <div 
            className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-line-sweep"
            style={{ animationDelay: '0.3s' }}
          />
          <div 
            className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent animate-line-sweep"
            style={{ animationDelay: '0.8s' }}
          />
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center">
            <div className="relative animate-scale-in">
              <div className="absolute -inset-12 rounded-full bg-primary/10 blur-3xl animate-glow-pulse" />
              <div className="absolute -inset-8 rounded-full bg-primary/15 blur-2xl animate-glow-pulse" style={{ animationDelay: '0.5s' }} />
              
              <svg 
                className="absolute -inset-6 w-[calc(100%+3rem)] h-[calc(100%+3rem)] animate-spin-slow"
                viewBox="0 0 100 100"
              >
                <circle cx="50" cy="50" r="46" fill="none" stroke="hsl(var(--primary)/0.15)" strokeWidth="1.5" />
                <circle cx="50" cy="50" r="46" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeDasharray="80 200" strokeLinecap="round" />
              </svg>
              
              <div className="flex h-28 w-28 items-center justify-center rounded-3xl bg-card border border-primary/20 shadow-2xl">
                <Rocket className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            <div className="mt-12 text-center space-y-4">
              <span 
                className="block text-xs font-bold uppercase tracking-[0.3em] text-primary opacity-0 animate-fade-in-up"
                style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
              >
                Phase 1
              </span>
              <h2 
                className="text-2xl md:text-3xl font-bold font-serif text-foreground opacity-0 animate-fade-in-up"
                style={{ animationDelay: '450ms', animationFillMode: 'forwards' }}
              >
                Entscheidung schärfen
              </h2>
              <p 
                className="text-sm text-muted-foreground opacity-0 animate-fade-in-up"
                style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}
              >
                Wir laden deinen Entscheidungsrahmen mit {inputCount} gewichteten Faktoren
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className={cn(
        "space-y-8 animate-fade-in transition-all duration-500",
        showTransition && "opacity-0 scale-95 blur-sm pointer-events-none"
      )}>
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <CheckCircle className="h-5 w-5" />
            </div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Analyse vorbereitet</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
            Der Entscheidungsrahmen
          </h1>
          <p className="text-sm text-muted-foreground">
            Prüfe kurz, ob die Zusammenfassung deine Situation korrekt erfasst.
          </p>
        </div>

        <div className="space-y-6">
          {/* ══ 2a: Two-Column Conflict Visualization ══ */}
          {(optionA || optionB) && (
            <div className="animate-fade-in space-y-3">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
                {/* Option A */}
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2 shadow-[0_0_20px_hsl(var(--primary)/0.05)]">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">Option A</span>
                  <p className="text-sm font-medium leading-relaxed text-foreground">
                    {optionA || "–"}
                  </p>
                  {stakesWrongChoiceA && (
                    <p className="text-xs text-muted-foreground leading-snug border-t border-border/40 pt-2 mt-2">
                      Risiko: {stakesWrongChoiceA}
                    </p>
                  )}
                </div>
                
                {/* VS Separator */}
                <div className="flex items-center justify-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted border border-border">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                </div>
                
                {/* Option B */}
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2 shadow-[0_0_20px_hsl(var(--primary)/0.05)]">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">Option B</span>
                  <p className="text-sm font-medium leading-relaxed text-foreground">
                    {optionB || "–"}
                  </p>
                  {stakesWrongChoiceB && (
                    <p className="text-xs text-muted-foreground leading-snug border-t border-border/40 pt-2 mt-2">
                      Risiko: {stakesWrongChoiceB}
                    </p>
                  )}
                </div>
              </div>

              {/* Scope label */}
              {decisionScope && (
                <div className="flex items-center gap-2 px-1">
                  <span className="text-xs font-medium text-muted-foreground">Geltungsbereich:</span>
                  <span className="text-xs font-semibold text-foreground">{decisionScope}</span>
                </div>
              )}
              
              {/* Portal Summary as contextualizing line */}
              <p className="text-sm leading-relaxed text-muted-foreground px-1">
                {summaryTyping.displayed}
                {!summaryTyping.isComplete && (
                  <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                )}
              </p>
            </div>
          )}

          {/* Fallback if no options in store: show summary in card */}
          {!optionA && !optionB && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-3 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="space-y-2 flex-1 min-w-0">
                  <h3 className="font-semibold text-base text-foreground">
                    Kurz zusammengefasst
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground/90">
                    {summaryTyping.displayed}
                    {!summaryTyping.isComplete && (
                      <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ══ 2b: Kernspannung – Tension Pairing ══ */}
          {displayedTension && (
            <div className="rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm p-5 space-y-4 animate-fade-in">
              {/* Section label */}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  Worum es im Kern geht
                </span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
              </div>
              
              {tensionPair ? (
                /* Single tension pairing: Left vs Right */
                <div className="flex items-center justify-center gap-4">
                  <div className="flex-1 text-right">
                    <p className="text-lg font-semibold text-foreground leading-snug">
                      {tensionPair.left}
                    </p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                    <Swords className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-lg font-semibold text-foreground leading-snug">
                      {tensionPair.right}
                      {!tensionTyping.isComplete && (
                        <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                /* Fallback: single tension statement */
                <div className="flex items-start gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary mt-0.5">
                    <Swords className="h-3.5 w-3.5" />
                  </div>
                  <p className="text-base font-medium leading-relaxed text-foreground">
                    {tensionTyping.displayed}
                    {!tensionTyping.isComplete && (
                      <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ══ 2c: Einstiegsfrage – Isolated Staging ══ */}
          {displayedQuestion && (
            <div className="space-y-4 animate-fade-in">
              {/* Phase Header */}
              <div className="flex items-center gap-3 pt-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                <span className="text-xs font-semibold text-primary uppercase tracking-widest px-3">
                  Phase 1 – Entscheidung schärfen
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
              </div>
              
              <div className="relative rounded-xl border-2 border-primary bg-card/50 backdrop-blur-sm p-6 md:p-8 space-y-4 shadow-[0_0_30px_hsl(var(--primary)/0.1)]">
                {/* Confidence chip positioned top-right */}
                {confidenceLabel && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="absolute top-4 right-4 cursor-help flex items-center gap-1">
                          <span className={cn(
                            "font-semibold px-2.5 py-1 rounded-full text-xs",
                            confidenceLabel.color, confidenceLabel.bg
                          )}>
                            Kontextklarheit: {confidenceLabel.label}
                          </span>
                          <Info className={cn("h-3 w-3", confidenceLabel.color)} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px]">
                        <p className="text-xs">Basierend auf Präzision & Konsistenz deiner Angaben</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                
                <div className="space-y-4 text-center pt-4">
                  <div className="flex items-center justify-center gap-2">
                    <Rocket className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Kernfrage deiner Entscheidung
                    </span>
                  </div>
                  <p className="text-xl md:text-2xl font-semibold leading-relaxed text-primary max-w-lg mx-auto">
                    {questionTyping.displayed}
                    {!questionTyping.isComplete && (
                      <span className="inline-block w-2 h-6 bg-primary ml-1 animate-pulse" />
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    {analysis?.whyThisQuestion || `Weil deine Entscheidung primär am Kontrast deiner ${inputCount} Eingaben hängt.`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ══ 3: CTA – Emotional Charge ══ */}
        {questionTyping.isComplete && (
          <div className={cn(
            "flex flex-col sm:flex-row gap-3 pt-2 animate-fade-in transition-opacity duration-300",
            isConfirming && "opacity-50 pointer-events-none"
          )}>
            <Button
              onClick={handleConfirm}
              disabled={isConfirming}
              className={cn(
                "h-12 px-8 text-base font-semibold flex-1 gap-2 rounded-xl transition-all duration-300",
                "shadow-[0_0_20px_hsl(var(--primary)/0.15)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.25)]",
                "hover:scale-[1.02] active:scale-[0.98]",
                isConfirming && "scale-95"
              )}
            >
              {isConfirming ? (
                <>
                  <CheckCircle className="h-5 w-5 animate-scale-in" />
                  Wird gestartet...
                </>
              ) : (
                <>
                  Phase 1 starten
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </Button>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleEditTrigger}
                disabled={isConfirming}
                className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4 transition-colors disabled:opacity-50"
              >
                Optionen bearbeiten
              </button>
              <span className="text-muted-foreground/30">·</span>
              <button
                onClick={handleEditStakes}
                disabled={isConfirming}
                className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4 transition-colors disabled:opacity-50"
              >
                Risiken anpassen
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
